/**
 * Lot definitions for the Marapa development.
 *
 * The LOTS array below is GENERATED — do not hand-edit the block between the
 * `<generated:lots>` / `</generated:lots>` markers. It is computed by
 * scripts/generate-lots.mjs from three inputs:
 *   1. the published Google Sheet dimensions (Ancho / Oeste / Este / Status),
 *   2. the surveyed corner points P2 / P11 / P1 (see src/data/refPoints.ts), and
 *   3. the street-width variable (STREET_WIDTH_M in the script, 12 m default).
 *
 * Model — anchored at the P2 corner:
 *   - EAST axis  = P2 -> P11 (top border);  SOUTH axis = P2 -> P1 (left border).
 *   - Rows stack from the top with a street between each:
 *         street / lots(C) / street / lots(B) / street / lots(A).
 *   - Within a row, lots tile EAST from the left border (width = Ancho); each
 *     lot drops SOUTH by Oeste (west corner) / Este (east corner).
 *
 * This is READ-ONLY at runtime — the app only reads the baked coordinates.
 * When the sheet or the street width changes, run `npm run generate:lots` to
 * recompute, then commit. See docs/lots-geometry.md.
 *
 * Status shown below is only the offline fallback; the live status comes from
 * the CSV Status column at runtime (see lotStatus / lotsApi.ts).
 */

export type LotStatus = 'available' | 'under-contract' | 'pending' | 'sold';
export interface LonLat { lon: number; lat: number }

export interface LotSpec {
  /** Row id in the public lots-meta sheet (A01, A02, …). Joins CSV dimensions onto the lot. */
  id: string;
  name?: string;
  status: LotStatus;
  /** Polygon corners (open ring — do NOT repeat the first vertex at the end). */
  corners: LonLat[];
  priceUsd?: number;
  buyer?: string | null;
  notes?: string;
}

/** Per-lot dimensional metadata pulled from the published Google Sheet. */
export interface LotMeta {
  id: string;       // CSV row id, e.g. "A01"
  lot?: string;     // CSV "Lot" name (e.g. "0-4")
  ancho?: number;   // width in metres
  oeste?: number;   // west edge length in metres
  este?: number;    // east edge length in metres
  area?: number;    // area in m² (authoritative when present)
  status?: LotStatus; // CSV "Status" — overrides LotSpec.status when present
}

export const LOTS: LotSpec[] = [
  // <generated:lots> — do not edit by hand; run `npm run generate:lots`
  {
    id: 'C01',
    name: "3-8-9",
    status: 'available',
    corners: [
      { lon: -63.9933050, lat: 10.8998147 },
      { lon: -63.9927697, lat: 10.8999237 },
      { lon: -63.9925311, lat: 10.8982217 },
      { lon: -63.9930664, lat: 10.8981127 },
    ],
  },
  {
    id: 'C02',
    name: "3-8-8-4-1-1 al 3-8-8-4-11",
    status: 'available',
    corners: [
      { lon: -63.9927697, lat: 10.8999237 },
      { lon: -63.9922343, lat: 10.9000328 },
      { lon: -63.9919957, lat: 10.8983307 },
      { lon: -63.9925311, lat: 10.8982217 },
    ],
  },
  {
    id: 'C03',
    name: "O-5",
    status: 'available',
    corners: [
      { lon: -63.9922343, lat: 10.9000328 },
      { lon: -63.9919042, lat: 10.9001000 },
      { lon: -63.9916656, lat: 10.8983980 },
      { lon: -63.9919957, lat: 10.8983307 },
    ],
  },
  {
    id: 'C04',
    name: "2-5-1-1 al 2-5-1-10",
    status: 'available',
    corners: [
      { lon: -63.9919042, lat: 10.9001000 },
      { lon: -63.9917191, lat: 10.9001377 },
      { lon: -63.9914805, lat: 10.8984357 },
      { lon: -63.9916656, lat: 10.8983980 },
    ],
  },
  {
    id: 'C05',
    name: "2-4-4-1 al 2-4-4-5",
    status: 'available',
    corners: [
      { lon: -63.9917191, lat: 10.9001377 },
      { lon: -63.9914439, lat: 10.9001937 },
      { lon: -63.9912053, lat: 10.8984917 },
      { lon: -63.9914805, lat: 10.8984357 },
    ],
  },
  {
    id: 'C06',
    name: "2-4-3",
    status: 'available',
    corners: [
      { lon: -63.9914439, lat: 10.9001937 },
      { lon: -63.9911681, lat: 10.9002499 },
      { lon: -63.9909295, lat: 10.8985479 },
      { lon: -63.9912053, lat: 10.8984917 },
    ],
  },
  {
    id: 'C07',
    name: "2-4-2",
    status: 'available',
    corners: [
      { lon: -63.9911681, lat: 10.9002499 },
      { lon: -63.9908923, lat: 10.9003061 },
      { lon: -63.9906537, lat: 10.8986040 },
      { lon: -63.9909295, lat: 10.8985479 },
    ],
  },
  {
    id: 'C08',
    name: "2-4-1",
    status: 'available',
    corners: [
      { lon: -63.9908923, lat: 10.9003061 },
      { lon: -63.9906165, lat: 10.9003622 },
      { lon: -63.9903779, lat: 10.8986602 },
      { lon: -63.9906537, lat: 10.8986040 },
    ],
  },
  {
    id: 'C09',
    name: "2-3-3-1",
    status: 'available',
    corners: [
      { lon: -63.9906165, lat: 10.9003622 },
      { lon: -63.9902495, lat: 10.9004370 },
      { lon: -63.9900108, lat: 10.8987350 },
      { lon: -63.9903779, lat: 10.8986602 },
    ],
  },
  {
    id: 'C10',
    name: "2-3-2",
    status: 'available',
    corners: [
      { lon: -63.9902495, lat: 10.9004370 },
      { lon: -63.9898824, lat: 10.9005118 },
      { lon: -63.9896438, lat: 10.8988097 },
      { lon: -63.9900108, lat: 10.8987350 },
    ],
  },
  {
    id: 'C11',
    name: "2-3-1",
    status: 'available',
    corners: [
      { lon: -63.9898824, lat: 10.9005118 },
      { lon: -63.9895153, lat: 10.9005865 },
      { lon: -63.9892767, lat: 10.8988845 },
      { lon: -63.9896438, lat: 10.8988097 },
    ],
  },
  {
    id: 'C12',
    name: "2-2-5-1 al 2-2-5-11",
    status: 'available',
    corners: [
      { lon: -63.9895153, lat: 10.9005865 },
      { lon: -63.9892952, lat: 10.9006314 },
      { lon: -63.9890566, lat: 10.8989293 },
      { lon: -63.9892767, lat: 10.8988845 },
    ],
  },
  {
    id: 'C13',
    name: "2-2-4-1 al 2-2-4-9",
    status: 'available',
    corners: [
      { lon: -63.9892952, lat: 10.9006314 },
      { lon: -63.9890750, lat: 10.9006762 },
      { lon: -63.9888364, lat: 10.8989742 },
      { lon: -63.9890566, lat: 10.8989293 },
    ],
  },
  {
    id: 'C14',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9890750, lat: 10.9006762 },
      { lon: -63.9874622, lat: 10.9010047 },
      { lon: -63.9872236, lat: 10.8993026 },
      { lon: -63.9888364, lat: 10.8989742 },
    ],
  },
  {
    id: 'B01',
    name: "O-1-1",
    status: 'available',
    corners: [
      { lon: -63.9930514, lat: 10.8980052 },
      { lon: -63.9926289, lat: 10.8980912 },
      { lon: -63.9923777, lat: 10.8962996 },
      { lon: -63.9928002, lat: 10.8962135 },
    ],
  },
  {
    id: 'B02',
    name: "O-1",
    status: 'available',
    corners: [
      { lon: -63.9926289, lat: 10.8980912 },
      { lon: -63.9923883, lat: 10.8981402 },
      { lon: -63.9921372, lat: 10.8963486 },
      { lon: -63.9923777, lat: 10.8962996 },
    ],
  },
  {
    id: 'B03',
    name: "O-12",
    status: 'available',
    corners: [
      { lon: -63.9923883, lat: 10.8981402 },
      { lon: -63.9922091, lat: 10.8981767 },
      { lon: -63.9919580, lat: 10.8963851 },
      { lon: -63.9921372, lat: 10.8963486 },
    ],
  },
  {
    id: 'B04',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9922091, lat: 10.8981767 },
      { lon: -63.9919224, lat: 10.8982351 },
      { lon: -63.9916713, lat: 10.8964435 },
      { lon: -63.9919580, lat: 10.8963851 },
    ],
  },
  {
    id: 'B05',
    name: "3-3-6",
    status: 'available',
    corners: [
      { lon: -63.9919224, lat: 10.8982351 },
      { lon: -63.9914500, lat: 10.8983313 },
      { lon: -63.9911988, lat: 10.8965397 },
      { lon: -63.9916713, lat: 10.8964435 },
    ],
  },
  {
    id: 'B06',
    name: "2-2-1 al 2-2-2",
    status: 'available',
    corners: [
      { lon: -63.9914500, lat: 10.8983313 },
      { lon: -63.9910315, lat: 10.8984165 },
      { lon: -63.9907804, lat: 10.8966249 },
      { lon: -63.9911988, lat: 10.8965397 },
    ],
  },
  {
    id: 'B07',
    name: "3-7-2-1 al 3-7-2-8",
    status: 'available',
    corners: [
      { lon: -63.9910315, lat: 10.8984165 },
      { lon: -63.9906250, lat: 10.8984993 },
      { lon: -63.9903739, lat: 10.8967077 },
      { lon: -63.9907804, lat: 10.8966249 },
    ],
  },
  {
    id: 'B08',
    name: "2-2-3-1 al 2-2-3-5",
    status: 'available',
    corners: [
      { lon: -63.9906250, lat: 10.8984993 },
      { lon: -63.9904159, lat: 10.8985419 },
      { lon: -63.9901647, lat: 10.8967503 },
      { lon: -63.9903739, lat: 10.8967077 },
    ],
  },
  {
    id: 'B09',
    name: "2-1-3-1 al 2-1-3-8",
    status: 'available',
    corners: [
      { lon: -63.9904159, lat: 10.8985419 },
      { lon: -63.9901507, lat: 10.8985959 },
      { lon: -63.9898995, lat: 10.8968043 },
      { lon: -63.9901647, lat: 10.8967503 },
    ],
  },
  {
    id: 'B10',
    name: "2-5-2-1 al 2-5-2-7",
    status: 'available',
    corners: [
      { lon: -63.9901507, lat: 10.8985959 },
      { lon: -63.9899764, lat: 10.8986314 },
      { lon: -63.9897253, lat: 10.8968398 },
      { lon: -63.9898995, lat: 10.8968043 },
    ],
  },
  {
    id: 'B11',
    name: "2-5-3-1 al 2-5-3-6",
    status: 'available',
    corners: [
      { lon: -63.9899764, lat: 10.8986314 },
      { lon: -63.9898022, lat: 10.8986669 },
      { lon: -63.9895510, lat: 10.8968753 },
      { lon: -63.9897253, lat: 10.8968398 },
    ],
  },
  {
    id: 'B12',
    name: "2-5-4 al 2-5-5",
    status: 'available',
    corners: [
      { lon: -63.9898022, lat: 10.8986669 },
      { lon: -63.9894534, lat: 10.8987379 },
      { lon: -63.9892023, lat: 10.8969463 },
      { lon: -63.9895510, lat: 10.8968753 },
    ],
  },
  {
    id: 'B13',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9894534, lat: 10.8987379 },
      { lon: -63.9893549, lat: 10.8987580 },
      { lon: -63.9891037, lat: 10.8969664 },
      { lon: -63.9892023, lat: 10.8969463 },
    ],
  },
  {
    id: 'B14',
    name: "2-5-6-1 al 2-5-6-3",
    status: 'available',
    corners: [
      { lon: -63.9893549, lat: 10.8987580 },
      { lon: -63.9891805, lat: 10.8987935 },
      { lon: -63.9889294, lat: 10.8970019 },
      { lon: -63.9891037, lat: 10.8969664 },
    ],
  },
  {
    id: 'B15',
    name: "2-6-1 al 2-6-4-1",
    status: 'available',
    corners: [
      { lon: -63.9891805, lat: 10.8987935 },
      { lon: -63.9880357, lat: 10.8990267 },
      { lon: -63.9877846, lat: 10.8972350 },
      { lon: -63.9889294, lat: 10.8970019 },
    ],
  },
  {
    id: 'A01',
    name: "O-4",
    status: 'sold',
    corners: [
      { lon: -63.9927851, lat: 10.8961061 },
      { lon: -63.9922296, lat: 10.8962192 },
      { lon: -63.9914950, lat: 10.8909787 },
      { lon: -63.9920065, lat: 10.8905520 },
    ],
  },
  {
    id: 'A02',
    name: "O-1",
    status: 'under-contract',
    corners: [
      { lon: -63.9922296, lat: 10.8962192 },
      { lon: -63.9916741, lat: 10.8963323 },
      { lon: -63.9910085, lat: 10.8915845 },
      { lon: -63.9914950, lat: 10.8909787 },
    ],
  },
  {
    id: 'A03',
    name: "0-4-5",
    status: 'sold',
    corners: [
      { lon: -63.9916741, lat: 10.8963323 },
      { lon: -63.9913426, lat: 10.8963998 },
      { lon: -63.9907838, lat: 10.8924135 },
      { lon: -63.9910085, lat: 10.8915845 },
    ],
  },
  {
    id: 'A04',
    name: "O-2",
    status: 'sold',
    corners: [
      { lon: -63.9913426, lat: 10.8963998 },
      { lon: -63.9910738, lat: 10.8964546 },
      { lon: -63.9905966, lat: 10.8930505 },
      { lon: -63.9907838, lat: 10.8924135 },
    ],
  },
  {
    id: 'A05',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9910738, lat: 10.8964546 },
      { lon: -63.9908319, lat: 10.8965039 },
      { lon: -63.9903547, lat: 10.8930998 },
      { lon: -63.9905966, lat: 10.8930505 },
    ],
  },
  {
    id: 'A06',
    name: "O-10",
    status: 'sold',
    corners: [
      { lon: -63.9908319, lat: 10.8965039 },
      { lon: -63.9906437, lat: 10.8965422 },
      { lon: -63.9902544, lat: 10.8937652 },
      { lon: -63.9904237, lat: 10.8935925 },
    ],
  },
  {
    id: 'A07',
    name: "1-1-1",
    status: 'available',
    corners: [
      { lon: -63.9906437, lat: 10.8965422 },
      { lon: -63.9904086, lat: 10.8965901 },
      { lon: -63.9900319, lat: 10.8939026 },
      { lon: -63.9902544, lat: 10.8937652 },
    ],
  },
  {
    id: 'A08',
    name: "1-1-2",
    status: 'available',
    corners: [
      { lon: -63.9904086, lat: 10.8965901 },
      { lon: -63.9901641, lat: 10.8966399 },
      { lon: -63.9898250, lat: 10.8942212 },
      { lon: -63.9900319, lat: 10.8939026 },
    ],
  },
  {
    id: 'A09',
    name: "1-1-3",
    status: 'available',
    corners: [
      { lon: -63.9901641, lat: 10.8966399 },
      { lon: -63.9898942, lat: 10.8966948 },
      { lon: -63.9895677, lat: 10.8943657 },
      { lon: -63.9898250, lat: 10.8942212 },
    ],
  },
  {
    id: 'A10',
    name: "1-1-4",
    status: 'available',
    corners: [
      { lon: -63.9898942, lat: 10.8966948 },
      { lon: -63.9896140, lat: 10.8967519 },
      { lon: -63.9893000, lat: 10.8945124 },
      { lon: -63.9895677, lat: 10.8943657 },
    ],
  },
  {
    id: 'A11',
    name: "O-8",
    status: 'available',
    corners: [
      { lon: -63.9896140, lat: 10.8967519 },
      { lon: -63.9893907, lat: 10.8967974 },
      { lon: -63.9891144, lat: 10.8948266 },
      { lon: -63.9893000, lat: 10.8945124 },
    ],
  },
  {
    id: 'A12',
    name: "O-7",
    status: 'available',
    corners: [
      { lon: -63.9893907, lat: 10.8967974 },
      { lon: -63.9891369, lat: 10.8968491 },
      { lon: -63.9889034, lat: 10.8951828 },
      { lon: -63.9891144, lat: 10.8948266 },
    ],
  },
  {
    id: 'A13',
    name: "O-6",
    status: 'available',
    corners: [
      { lon: -63.9891369, lat: 10.8968491 },
      { lon: -63.9888368, lat: 10.8969102 },
      { lon: -63.9886484, lat: 10.8955665 },
      { lon: -63.9889034, lat: 10.8951828 },
    ],
  },
  {
    id: 'A14',
    name: "O-4",
    status: 'available',
    corners: [
      { lon: -63.9888368, lat: 10.8969102 },
      { lon: -63.9883888, lat: 10.8970014 },
      { lon: -63.9882004, lat: 10.8956577 },
      { lon: -63.9886484, lat: 10.8955665 },
    ],
  },
  // </generated:lots>
];

export function getAllLotPolygons() {
  return LOTS.map((lot) => ({ lot, corners: lot.corners }));
}

/** Polygon area in m² using equirectangular projection at the centroid. */
export function computeAreaM2(corners: LonLat[]): number {
  if (corners.length < 3) return 0;
  const meanLat = corners.reduce((s, c) => s + c.lat, 0) / corners.length;
  const cosLat = Math.cos((meanLat * Math.PI) / 180);
  const M_PER_DEG_LON = 111320 * cosLat;
  const M_PER_DEG_LAT_LOCAL = 110574;
  // Shoelace on x = lon*M_PER_DEG_LON, y = lat*M_PER_DEG_LAT
  let twiceArea = 0;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const ax = a.lon * M_PER_DEG_LON;
    const ay = a.lat * M_PER_DEG_LAT_LOCAL;
    const bx = b.lon * M_PER_DEG_LON;
    const by = b.lat * M_PER_DEG_LAT_LOCAL;
    twiceArea += ax * by - bx * ay;
  }
  return Math.abs(twiceArea) / 2;
}

// --- Selected-lot pub-sub store ---
type SelectedLotListener = (lot: LotSpec | null) => void;
let _selected: LotSpec | null = null;
const _listeners = new Set<SelectedLotListener>();
export const selectedLotStore = {
  get: () => _selected,
  set(lot: LotSpec | null) {
    if (_selected === lot) return;
    _selected = lot;
    for (const cb of _listeners) cb(lot);
  },
  subscribe(cb: SelectedLotListener) {
    _listeners.add(cb);
    cb(_selected);
    return () => _listeners.delete(cb);
  },
};

export function findLotById(id: string): LotSpec | null {
  return LOTS.find((l) => l.id === id) ?? null;
}

/** Human-facing label combining the lot id and its internal name, e.g.
 *  `A01 (0-4)`. Falls back to the id alone when no name is set. */
export function lotLabel(lot: LotSpec): string {
  if (lot.name) return `${lot.id} (${lot.name})`;
  return lot.id;
}

// --- Lot-metadata pub-sub store ---
// Loaded from the published CSV on app start (see src/data/lotsApi.ts).
// Keyed by lot id (matches LotSpec.id, which equals the CSV row id).
type LotMetaListener = (meta: ReadonlyMap<string, LotMeta>) => void;
const _lotMeta = new Map<string, LotMeta>();
const _metaListeners = new Set<LotMetaListener>();
export const lotMetaStore = {
  get: () => _lotMeta as ReadonlyMap<string, LotMeta>,
  getById: (id: string | undefined) =>
    id ? _lotMeta.get(id) : undefined,
  setAll(entries: Iterable<readonly [string, LotMeta]>) {
    _lotMeta.clear();
    for (const [k, v] of entries) _lotMeta.set(k, v);
    for (const cb of _metaListeners) cb(_lotMeta);
  },
  subscribe(cb: LotMetaListener) {
    _metaListeners.add(cb);
    cb(_lotMeta);
    return () => _metaListeners.delete(cb);
  },
};

/** Resolve a lot's effective status: the CSV-published value if loaded,
 *  otherwise the hardcoded fallback on the LotSpec. */
export function lotStatus(lot: LotSpec): LotStatus {
  return lotMetaStore.getById(lot.id)?.status ?? lot.status;
}
